import { useCallback } from "react";
import { useDispatch } from "react-redux";
import { useLoginMutation } from "./slice/auth-api";
import { clearToken, setToken, setUser } from "./slice/auth-slice";

import { useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { apiSlice } from "../services/http";
import { featureFlags } from "@/config/feature-flag";
import { keycloak } from "@/data/keycloak";
interface IRole {
  name: string;
  permissions: string[];
}
export interface IUser {
  id: string;
  name: string;
  superuser: boolean;
  role: IRole;
}

export function useAuth() {
  const dispatch = useDispatch();
  const [login, { isLoading }] = useLoginMutation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const signIn = useCallback(
    async ({
      email,
      password,
      token,
    }: {
      email: string;
      password: string;
      token: string;
    }) => {
      try {
        const response = await login({ email, password, token }).unwrap();

        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const { access_token, refresh_token, user } = (response as any) ?? {};

        localStorage.setItem("@azure:token", access_token);
        localStorage.setItem("@azure:refresh_token", refresh_token);
        localStorage.setItem("@azure:user", JSON.stringify(user) || "{}");

        dispatch(setToken(refresh_token));
        dispatch(setUser(user));
      } catch (error) {
        console.error("Failed to login:", error);
        throw error;
      }
    },
    [dispatch, login]
  );

  const signOut = useCallback(async () => {
    try {
      console.log("[Auth] Iniciando logout...");

      // Limpa queries e estado do Redux
      await queryClient.invalidateQueries();
      dispatch(apiSlice.util.resetApiState());
      dispatch(clearToken());

      if (featureFlags.USE_KEYCLOAK) {
        console.log("[Auth] Logout com Keycloak ativado");

        // Limpa localStorage antes de chamar keycloak.logout
        localStorage.clear();

        // Chama logout do Keycloak para encerrar a sessão no servidor
        if (keycloak.authenticated) {
          console.log("[Auth] Chamando keycloak.logout()...");
          await keycloak.logout({
            redirectUri: import.meta.env.VITE_HUB_URL || window.location.origin,
          });
        } else {
          console.log("[Auth] Usuário não autenticado no Keycloak, redirecionando...");
          window.location.href = import.meta.env.VITE_HUB_URL || "/";
        }
      } else {
        console.log("[Auth] Logout sem Keycloak");
        localStorage.clear();
        navigate("/login");
      }
    } catch (error) {
      console.error("[Auth] Logout error: ", error);
      // Em caso de erro, garante que o usuário seja redirecionado
      localStorage.clear();
      window.location.href = import.meta.env.VITE_HUB_URL || "/login";
    }
  }, [dispatch, queryClient, navigate]);

  return {
    signIn,
    signOut,
    isLoading,
  };
}
